'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import {
  Box,
  Button2,
  Dialog,
  DialogActions,
  DialogFooter,
  DialogHeader,
  noop,
} from '@frameio/vapor';
import { useWatermarkTemplateCompleteFragment } from '../../gql/fragments/generated/watermark_templates';
import { useWatermarkTemplates } from '../../hooks/useWatermarkTemplates';
import {
  ASPECT_RATIOS,
  MODAL_HEIGHT,
  MODAL_MAX_WIDTH,
  MODAL_WIDTH,
  WatermarkBuilderModes,
  WatermarkModalModes,
} from './constants';
import TEXT from './generated/TEXT/WatermarkTemplateManagerDialog';
import { FooterControls } from './TemplateBuilder/components/FooterControls';
import { WatermarkTemplateBuilder } from './TemplateBuilder/WatermarkTemplateBuilder';
import { WatermarkTemplateSelector } from './TemplatePreview/WatermarkTemplateSelector';
import type { AspectRatio } from './types';
import {
  calculateChildRectangle,
  calculateNewPosition,
  clampBlockPositionToBounds,
  transformPosition,
} from './utils/builderUtils';
import {
  transformBackendWatermarkTemplate,
  transformFrontendDataForMutation,
} from './utils/dataTransformer';

TEXT.DEFINE({
  /** Error messages for form validation */
  errors: {
    /** Error when template name exceeds length limit */
    templateNameTooLong: 'Template name is too long',
    /** Error when template name already exists */
    templateNameExists: 'Template name already exists',
    /** Error when required field is missing */
    required: (field: string) => `${field} is required`,
  },
  /** Title for watermark templates section */
  watermarkTemplates: 'Watermark Templates',
  /** Cancel button label */
  cancel: 'Cancel',
  /** Apply button label */
  apply: 'Apply',
  /** Save button label */
  save: 'Save',
  /** Create button label */
  create: 'Create',
  /** Back button label */
  back: 'Back',
  /** Close button label */
  close: 'Close',
  /** Label for creating new template */
  createNewTemplate: 'New Watermark Template',
  /** Label for editing template */
  editTemplate: 'Edit Watermark Template',
});

type WatermarkTemplateManagerDialogProps = {
  /** The text to display on the button that triggers the modal to display. */
  triggerButtonText?: string;
  /** The currently selected watermark template id. */
  selectedTemplateId: string;
  /** The header text for the dialog. */
  dialogHeader?: string;
  /** Whether the user has permission to create, edit, and delete templates. */
  canManageTemplates?: boolean;
  /** Whether or not the dialog is currently showing. */
  isShowing: boolean;
  /** A function to call when the dialog is closed. */
  onRequestClose: VoidFunction;
  /** A function to call when a template is selected. */
  onTemplateSelect: (template: { id: string; name: string }) => void;
};

export const variants = {
  enter: {
    opacity: 0,
  },
  center: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
};

const getDialogTitle = ({
  modalMode,
  builderMode,
  dialogHeader,
}: {
  modalMode: WatermarkModalModes;
  builderMode: WatermarkBuilderModes;
  dialogHeader?: string;
}): string => {
  if (modalMode === WatermarkModalModes.BUILDER) {
    return builderMode === WatermarkBuilderModes.CREATE
      ? TEXT.createNewTemplate
      : TEXT.editTemplate;
  }

  const selectionTitle = dialogHeader
    ? `${TEXT.watermarkTemplates}: ${dialogHeader}`
    : TEXT.watermarkTemplates;

  return modalMode === WatermarkModalModes.SELECTION
    ? selectionTitle
    : TEXT.watermarkTemplates;
};

export const WatermarkTemplateManagerDialog = ({
  selectedTemplateId,
  dialogHeader,
  canManageTemplates = true,
  isShowing = false,
  onRequestClose = noop,
  onTemplateSelect = noop,
}: WatermarkTemplateManagerDialogProps) => {
  const watermarkTemplates = useWatermarkTemplates();

  const { nodes: templates = [], totalCount: totalTemplatesCount = 0 } =
    watermarkTemplates.connection ?? {};

  const templateFragment =
    useWatermarkTemplateCompleteFragment(selectedTemplateId);

  const selectedTemplate = templateFragment.complete
    ? templateFragment.data
    : null;

  const [scale, setScale] = useState(1);
  const [modalMode, setModalMode] = useState<WatermarkModalModes>(
    WatermarkModalModes.SELECTION
  );
  const [builderMode, setBuilderMode] = useState<WatermarkBuilderModes>(
    WatermarkBuilderModes.CREATE
  );
  // State for the draft watermark item selected in the list.
  const [watermarkSelectionSet, setWatermarkSelectionSet] = useState(
    new Set([selectedTemplateId])
  );
  // Scroll to a specific template in the template selector.
  const [scrolledId, scrollToId] = useState(null);

  const [isSafeZoneVisible, setIsSafeZoneVisible] = useReducer(
    (prevState) => !prevState,
    false
  );

  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    ASPECT_RATIOS.WIDE
  );

  const [previewDimensions, setPreviewDimensions] = useState({
    width: 0,
    height: 0,
  });

  const [templateData, setTemplateData] = useState({
    id: null,
    templateName: '',
    blocks: [],
  });

  const overrideTemplateSelectionByName = useRef<string>(null);
  const previewColumnRef = useRef<HTMLDivElement>(null);

  // Set and scroll to the selected template when it changes.
  useEffect(() => {
    if (selectedTemplateId) {
      setWatermarkSelectionSet(new Set([selectedTemplateId]));
      scrollToId(selectedTemplateId);
    }
  }, [selectedTemplateId]);

  useEffect(() => {
    if (modalMode === WatermarkModalModes.SELECTION) {
      // Scroll to the selected template in the template selector
      scrollToId(watermarkSelectionSet.values().next().value ?? null);
    }
    // We only want to run this effect when the modalMode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalMode]);

  /**
   * When we receive an update to our templates list, chances are a new template was
   * created or an existing template was updated. If that's the case, we want to select
   * it in the template selection list, overriding the previously selected template.
   */
  useEffect(() => {
    if (overrideTemplateSelectionByName.current) {
      const targetTemplateId = templates.find(
        ({ name }) => name === overrideTemplateSelectionByName.current
      )?.id;
      if (targetTemplateId && !targetTemplateId.includes('optimistic')) {
        setWatermarkSelectionSet(new Set([targetTemplateId]));
        scrollToId(targetTemplateId);
        overrideTemplateSelectionByName.current = null;
      }
    }
  }, [templates]);

  const selectedWatermark = useMemo(() => {
    const currentWatermarkId = watermarkSelectionSet.values().next().value;
    const matchingTemplate = templates?.filter(
      ({ id }) => id === currentWatermarkId
    )[0];
    return (
      matchingTemplate ||
      (selectedTemplate?.deletedAt ? selectedTemplate : null)
    );
  }, [watermarkSelectionSet, templates, selectedTemplate]);

  const { formState, register, resetField, setValue, setFocus } = useForm({
    criteriaMode: 'all',
    defaultValues: { templateName: '', customText: 'Your Custom Text' },
    mode: 'all',
  });

  /**
   * constructBackendFriendlyData
   * Converts the block position values from frontend to backend friendly values.
   * @param {WatermarkBlockData[]} blocks - An array of blocks to be transformed.
   * @returns {WatermarkBlockData[]} - A new array of blocks with updated position values.
   */
  const constructBackendFriendlyData = useCallback(
    (blocks) => {
      return blocks?.map((block) => {
        const blockElement = document.getElementById(block.id);
        if (!blockElement) return block;

        const { width: elementWidth, height: elementHeight } =
          blockElement.getBoundingClientRect();
        const { x, y } = transformPosition(
          block.position.x,
          block.position.y,
          previewDimensions.width / scale,
          previewDimensions.height / scale,
          block.section,
          elementWidth / scale,
          elementHeight / scale,
          block.rotation,
          true
        );
        return { ...block, position: { x, y } };
      });
    },
    [previewDimensions, scale]
  );

  const clearBuilderData = useCallback(() => {
    setTemplateData({
      id: null,
      templateName: '',
      blocks: [],
    });
    resetField('templateName');
  }, [resetField]);

  const handleOpenTemplateBuilder = useCallback(() => {
    clearBuilderData();
    setBuilderMode(WatermarkBuilderModes.CREATE);
    setModalMode(WatermarkModalModes.BUILDER);
  }, [clearBuilderData]);

  const handleEditTemplateInBuilder = useCallback(
    (item) => {
      setBuilderMode(WatermarkBuilderModes.EDIT);
      // Find the template matching the item.id we're passed
      const template = templates.find(({ id }) => id === item.id);
      const transformedTemplate = transformBackendWatermarkTemplate(template);
      setTemplateData({
        id: item.id,
        templateName: transformedTemplate.templateName,
        blocks: transformedTemplate.blocks,
      });
      setValue('templateName', transformedTemplate.templateName);
      setModalMode(WatermarkModalModes.BUILDER);
    },
    [templates, setValue]
  );

  const handleCreateNewTemplate = () => {
    const normalizedBlocks = constructBackendFriendlyData(templateData.blocks);
    const backendFriendlyTemplate = transformFrontendDataForMutation({
      ...templateData,
      normalizedBlocks,
    });
    watermarkTemplates.add(backendFriendlyTemplate);
    overrideTemplateSelectionByName.current = templateData.templateName;
    setModalMode(WatermarkModalModes.SELECTION);
    clearBuilderData();
  };

  const handleUpdateWatermark = () => {
    if (!templateData.id) return;

    const normalizedBlocks = constructBackendFriendlyData(templateData.blocks);
    const backendFriendlyTemplate = transformFrontendDataForMutation({
      ...templateData,
      normalizedBlocks,
    });
    watermarkTemplates.update(templateData.id, backendFriendlyTemplate);
    overrideTemplateSelectionByName.current = templateData.templateName;
    setModalMode(WatermarkModalModes.SELECTION);
    clearBuilderData();
  };

  const handleApplyWatermark = useCallback(() => {
    onTemplateSelect?.({
      id: selectedWatermark.id,
      name: selectedWatermark.name,
    });
    onRequestClose?.();
  }, [onTemplateSelect, onRequestClose, selectedWatermark]);

  const updateBlocksForAspectRatio = useCallback(
    ({ oldBlocks, oldWidth, oldHeight, newWidth, newHeight, newScale }) => {
      const newBlocks = oldBlocks.map((block) => {
        const newPosition = calculateNewPosition(
          block.position.x,
          block.position.y,
          oldWidth / newScale,
          oldHeight / newScale,
          newWidth / newScale,
          newHeight / newScale
        );

        // Nudge the block back into view so its anchor stays within the new
        // frame after the aspect ratio (and preview dimensions) change.
        const blockEl = document.getElementById(block.id);
        if (!blockEl) {
          return { ...block, position: newPosition };
        }
        const { width: elementWidth, height: elementHeight } =
          blockEl.getBoundingClientRect();

        return {
          ...block,
          position: clampBlockPositionToBounds({
            position: newPosition,
            section: block.section,
            rotation: block.rotation,
            containerWidth: newWidth / newScale,
            containerHeight: newHeight / newScale,
            elementWidth: elementWidth / newScale,
            elementHeight: elementHeight / newScale,
          }),
        };
      });

      return {
        blocks: newBlocks,
      };
    },
    []
  );

  // Handle aspect ratio change
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleAspectRatioChange = useCallback(
    (newAspectRatio: AspectRatio) => {
      if (!previewColumnRef.current) return;
      const { width: columnWidth, height: columnHeight } =
        previewColumnRef.current.getBoundingClientRect();
      const { width: oldWidth, height: oldHeight } = calculateChildRectangle(
        columnWidth,
        columnHeight,
        aspectRatio
      );
      const { width: newWidth, height: newHeight } = calculateChildRectangle(
        columnWidth,
        columnHeight,
        newAspectRatio
      );

      setAspectRatio(newAspectRatio);
      setTemplateData((prev) => ({
        ...prev,
        ...updateBlocksForAspectRatio({
          oldBlocks: prev.blocks,
          oldWidth,
          oldHeight,
          newWidth,
          newHeight,
          newScale: scale,
        }),
      }));
      setPreviewDimensions({ width: newWidth, height: newHeight });
    },
    [aspectRatio, scale, setTemplateData, updateBlocksForAspectRatio]
  );

  const handleOnRequestClose = useCallback(() => {
    onRequestClose();
    clearBuilderData();
  }, [onRequestClose, clearBuilderData]);

  const handleCancelBuilder = useCallback(() => {
    clearBuilderData();
    setModalMode(WatermarkModalModes.SELECTION);
  }, [clearBuilderData]);

  const doesTemplateContainAnyEmptyBlocks = useCallback((template) => {
    return template?.blocks?.some((block) => {
      const isCustomTextEmpty = block?.customText?.trim() === '';
      const allDataPointFieldsFalse =
        !block?.displayName &&
        !block?.displayEmail &&
        !block?.displayIP &&
        !block?.displayDateTime &&
        !block?.displayShareCreator;
      return isCustomTextEmpty && allDataPointFieldsFalse;
    });
  }, []);

  return (
    <Dialog
      enableClickOutsideToClose={false}
      isShowing={isShowing}
      onExited={() => setModalMode(WatermarkModalModes.SELECTION)}
      onRequestClose={handleOnRequestClose}
      size="large"
      style={{ width: MODAL_WIDTH, maxWidth: MODAL_MAX_WIDTH }}
    >
      <DialogHeader
        closeLabel={TEXT.close}
        title={getDialogTitle({ modalMode, builderMode, dialogHeader })}
      />
      <Box minHeight={MODAL_HEIGHT} overflow="hidden" position="relative">
        <AnimatePresence
          initial={false}
          onExitComplete={() => {
            if (modalMode === WatermarkModalModes.BUILDER)
              setFocus('templateName');
          }}
        >
          {modalMode === WatermarkModalModes.SELECTION && (
            <WatermarkTemplateSelector
              key="selection"
              canManageTemplates={canManageTemplates}
              deleteTemplate={watermarkTemplates.delete}
              handleCreateNewTemplate={handleOpenTemplateBuilder}
              handleEditTemplate={handleEditTemplateInBuilder}
              hasSessionBasedTemplates={
                watermarkTemplates.hasSessionBasedTemplates
              }
              isLoading={watermarkTemplates.loading}
              isShowing={isShowing}
              scrolledId={scrolledId}
              scrollToId={scrollToId}
              selectedWatermark={selectedWatermark}
              setWatermarkSelectionSet={setWatermarkSelectionSet}
              templates={templates}
              totalTemplatesCount={totalTemplatesCount}
              watermarkSelectionSet={watermarkSelectionSet}
            />
          )}
          {modalMode === WatermarkModalModes.BUILDER && (
            <WatermarkTemplateBuilder
              key="builder"
              aspectRatio={aspectRatio}
              builderMode={builderMode}
              constructBackendFriendlyData={constructBackendFriendlyData}
              formState={formState}
              isSafeZoneVisible={isSafeZoneVisible}
              previewColumnRef={previewColumnRef}
              previewDimensions={previewDimensions}
              register={register}
              scale={scale}
              setPreviewDimensions={setPreviewDimensions}
              setScale={setScale}
              setTemplateData={setTemplateData}
              templateData={templateData}
              templates={templates}
            />
          )}
        </AnimatePresence>
      </Box>

      <DialogFooter>
        <AnimatePresence>
          {modalMode === WatermarkModalModes.SELECTION && (
            <DialogActions>
              <motion.div
                key="footer-controls-1"
                animate="center"
                exit="exit"
                initial="enter"
                transition={{
                  ease: 'easeInOut',
                  duration: 0.2,
                }}
                variants={variants}
              >
                <StyledButton onPress={onRequestClose}>
                  {TEXT.cancel}
                </StyledButton>
                <Button2
                  data-testid="apply-watermark-template-button"
                  isDisabled={
                    watermarkTemplates.loading ||
                    !selectedWatermark ||
                    Boolean(selectedWatermark?.deletedAt)
                  }
                  onPress={handleApplyWatermark}
                  variant="primary"
                >
                  {TEXT.apply}
                </Button2>
              </motion.div>
            </DialogActions>
          )}
          {modalMode === WatermarkModalModes.BUILDER && (
            <motion.div
              key="footer-controls-2"
              animate="center"
              exit="exit"
              initial="enter"
              transition={{
                ease: 'easeInOut',
                duration: 0.2,
              }}
              variants={variants}
            >
              <FooterControls
                aspectRatio={aspectRatio}
                handleAspectRatioChange={handleAspectRatioChange}
                isSafeZoneVisible={isSafeZoneVisible}
                setIsSafeZoneVisible={setIsSafeZoneVisible}
              />
            </motion.div>
          )}

          {modalMode === WatermarkModalModes.BUILDER && (
            <DialogActions>
              <motion.div
                key="footer-controls-3"
                animate="center"
                exit="exit"
                initial="enter"
                transition={{
                  ease: 'easeInOut',
                  duration: 0.2,
                }}
                variants={variants}
              >
                <StyledButton onPress={handleCancelBuilder}>
                  {TEXT.cancel}
                </StyledButton>
                {builderMode === WatermarkBuilderModes.CREATE && (
                  <Button2
                    data-testid="create-watermark-template-button"
                    isDisabled={
                      !templateData?.templateName ||
                      !formState?.isValid ||
                      watermarkTemplates.loading ||
                      doesTemplateContainAnyEmptyBlocks(templateData)
                    }
                    onPress={handleCreateNewTemplate}
                    variant="primary"
                  >
                    {TEXT.create}
                  </Button2>
                )}
                {builderMode === WatermarkBuilderModes.EDIT && (
                  <Button2
                    data-testid="save-watermark-template-button"
                    isDisabled={
                      !templateData?.templateName ||
                      !formState?.isValid ||
                      watermarkTemplates.loading ||
                      doesTemplateContainAnyEmptyBlocks(templateData)
                    }
                    onPress={handleUpdateWatermark}
                    variant="primary"
                  >
                    {TEXT.save}
                  </Button2>
                )}
              </motion.div>
            </DialogActions>
          )}
        </AnimatePresence>
      </DialogFooter>
    </Dialog>
  );
};

const StyledButton = styled(Button2)`
  margin-right: 6px;
`;
